<template>
  <div :class="{ 'container mx-auto': true }">
    <div
      :class="{
        'content-wrapper mx-auto max-w-320px lg:max-w-1120px': true,
      }"
    >
      <div
        :class="{
          'flex flex-col items-center gap-9 lg:flex-row lg:gap-100px': true,
          'lg:flex-row-reverse': imagesToTheRight,
        }"
      >
        <div
          :class="{
            'relative mx-auto w-full lg:w-1/3': true,
          }"
        >
          <div
            :class="{
              'testimonial-images relative mx-auto w-max': true,
            }"
          >
            <div
              :class="{
                'img-background relative mx-auto h-230px w-230px lg:h-320px lg:w-320px': true,
                'animate-slide-in-tr': animated,
              }"
            >
              <PtcPicture
                v-if="testimonialData?.backgroundImage && testimonialData.backgroundImage.asset"
                :src="testimonialData.backgroundImage.asset?._ref"
                provider="sanity"
                :alt="resolvedBackground?.altText || 'background image'"
                :quality="75"
                :img-attrs="{
                  class: {
                    'border-2 border-solid border-www-color-primary-lighter rounded-50%': true,
                  },
                }"
              />
            </div>
            <div
              :class="{
                'img-foreground absolute bottom-0 right-0 h-130px w-130px lg:h-180px lg:w-170px': true,
                'animate-slide-in-small': animated,
              }"
            >
              <PtcPicture
                v-if="testimonialData?.person.reference?.headshot.asset"
                :src="testimonialData.person.reference.headshot.asset?._ref"
                provider="sanity"
                :alt="resolvedForeground?.altText || 'person headshot'"
                :quality="75"
                :img-attrs="{
                  class: {
                    'border-2 border-solid border-www-color-primary-lighter rounded-50%': true,
                  },
                }"
              />
            </div>
          </div>
        </div>
        <div
          :class="{
            'w-full lg:w-2/3': true,
          }"
        >
          <PtcPicture
            v-if="testimonialData?.person.reference?.companyLogo?.asset"
            :src="testimonialData?.person.reference?.companyLogo?.asset?._ref"
            :alt="resolvedCompany?.altText || 'company logo'"
            provider="sanity"
            :class="{
              'mb-9 mt-9 block max-h-11 max-w-150px lg:mb-10 lg:hidden': true,
            }"
            :img-attrs="{ class: 'max-h-11 md:max-h-16 lg:max-h-18' }"
            :modifiers="{
              w: 150,
            }"
            :quality="75"
          />
          <p
            :class="{
              'relative pb-5 text-base leading-5 lg:text-lg lg:leading-8': true,
              'after:absolute after:bottom-0 after:left-0 after:h-1 after:w-52px after:bg-www-color-neutral-very-light': true,
            }"
          >
            <PtcIcon
              name="ptc-quotes"
              :class="{
                'h-5 w-5 text-www-color-primary-lighter': true,
              }"
              alt="Quotes icon"
            />
            {{ testimonialData?.quote }}<span>’’</span>
          </p>
          <div
            v-if="testimonialData?.person.reference"
            :class="{
              'flex flex-col lg:mt-5 lg:flex-row lg:justify-between': true,
            }"
          >
            <div
              :class="{
                'mb-5 mt-5 flex flex-col text-left lg:mb-0 lg:mt-0': true,
              }"
            >
              <span
                :class="{
                  'relative mb-10px text-lg font-extrabold uppercase leading-5': true,
                }"
                >{{ testimonialData?.person.reference?.name }}</span
              >
              <div
                :class="{
                  'flex items-center': true,
                }"
              >
                <span
                  :class="{
                    'relative mr-10px pr-10px text-xs leading-4': true,
                    'after:absolute after:bottom-2px after:ml-10px after:h-4 after:border-2 after:border-solid after:border-www-color-neutral-very-light': true,
                  }"
                  >{{ testimonialData?.person.reference?.jobTitle }}</span
                >
                <span
                  :class="{
                    'ml-1 text-xs font-bold leading-4': true,
                  }"
                  >{{ testimonialData?.person.reference?.company }}</span
                >
              </div>
              <GeneralLink
                v-if="testimonialData?.link"
                v-bind="testimonialData.link"
                :class="{
                  'mt-5 font-ptc-Rajdhani text-xs font-bold uppercase text-www-color-primary-lighter hover:underline': true,
                }"
              />
            </div>
            <div
              :class="{
                right: true,
              }"
            ></div>
            <PtcPicture
              v-if="testimonialData?.person.reference?.companyLogo?.asset"
              :src="testimonialData?.person.reference?.companyLogo?.asset?._ref"
              :alt="resolvedCompany?.altText || 'company logo'"
              provider="sanity"
              :img-attrs="{
                class: 'hidden max-h-11 md:max-h-16 lg:max-h-70px lg:block',
              }"
              :class="{
                'hidden max-h-70px max-w-150px lg:block': true,
              }"
              :quality="75"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import PtcPicture from '~/components/Atoms/PtcPicture/PtcPicture.vue';
import PtcIcon from '~/components/Atoms/PtcIcon/PtcIcon.vue';
import GeneralLink from '~/components/Molecules/HeaderComponents/GeneralLink.vue';
import type { TestimonialRefQueryResult } from '~/sanity/types';

interface Props {
  testimonialData?: TestimonialRefQueryResult;
  animated?: boolean;
  imagesToTheRight?: boolean;
}

const props = defineProps<Props>();
const { getResolvedImage } = useRefsStore();
const resolvedBackground = await getResolvedImage(
  props.testimonialData?.backgroundImage?.asset?._ref
);
const resolvedForeground = await getResolvedImage(
  props.testimonialData?.person?.reference?.headshot.asset?._ref
);
const resolvedCompany = await getResolvedImage(
  props.testimonialData?.person.reference?.companyLogo?.asset?._ref
);
</script>

<style>
.quotes:after {
  content: '’’' !important;
}
</style>
